import { Emitter } from "@core/events/emitter";
import type { SceneInfo } from "@models/data/sceneInfo";
import type { RgbaColor, SkyboxData, SkyboxMode } from "@models/data/skyboxData";
import {
  SKYBOX_MODE,
  skyboxColorForMode,
} from "@models/data/skyboxData";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { ISceneModel } from "./ISceneModel";

/**
 * Snapshot of what the viewport should render as the scene backdrop.
 * Web equivalent of the cached fields on Unity's `SkyboxModel`
 * (`m_mode` / `m_color` / `m_rotation` / `m_scale` / `m_intensity` +
 * the resolved texture).
 */
export interface SkyboxRenderState {
  readonly mode: SkyboxMode;
  readonly color: RgbaColor;
  /** Degrees around the vertical axis. */
  readonly rotation: number;
  readonly scale: { readonly x: number; readonly y: number };
  readonly intensity: number;
  /** Resolved texture URL for `Custom` mode (may lag one refresh while downloading). */
  readonly textureUrl: string | null;
  /** File extension of the custom texture (`".hdr"` needs the RGBE loader). */
  readonly textureFormat: string | null;
}

export interface ISkyboxModel {
  /** Emits the freshly computed render state (Unity `SkyboxChanged`). */
  readonly onSkyboxChanged: Emitter<SkyboxRenderState>;

  readonly state: SkyboxRenderState;
  /** Unity `ISkyboxModel.IsDefaultSkybox`. */
  readonly isDefaultSkybox: boolean;

  /**
   * Persist new skybox values for the active scene and re-apply. Port of
   * `SkyboxModel.UpdateSkyboxData` (`SkyboxModel.cs:285`): texture id is
   * kept, the scene row is updated, and a mode flip re-derives the default
   * environment (table visibility).
   */
  updateSkyboxData(skybox: SkyboxData): Promise<void>;

  /** Re-derive the render state from a scene row (Unity `RefreshSkybox`). */
  refreshSkybox(scene: SceneInfo | null): void;

  // Live (non-persisting) appliers — ports of Unity `SetDefaultSkybox` /
  // `SetSolidSkybox` / `SetActiveSceneCustomSkybox`. The inspector uses
  // these for immediate feedback while edits are still in flight; the
  // authoritative write goes through {@link updateSkyboxData}.

  setDefaultSkybox(): void;
  setSolidSkybox(color: RgbaColor, intensity: number): void;
  setActiveSceneCustomSkybox(
    color: RgbaColor,
    rotation: number,
    scale: { x: number; y: number },
    intensity: number,
  ): void;
}

const DEFAULT_STATE: SkyboxRenderState = {
  mode: SKYBOX_MODE.None,
  // Unity `SkyboxModel.m_defaultBackgroundColor` (0.1490, 0.1607, 0.1647).
  color: { r: 0.149, g: 0.1607, b: 0.1647, a: 1 },
  rotation: 0,
  scale: { x: 1, y: 1 },
  intensity: 1,
  textureUrl: null,
  textureFormat: null,
};

/**
 * `SkyboxModel` — headless port of Unity's `SkyboxModel`
 * (`Models/SceneModel/SkyboxModel.cs`). Owns skybox *state* + persistence;
 * the Three.js application of that state lives in
 * `runtime/assets/sceneEnvironmentRenderer.ts` (models never touch
 * renderer state directly).
 *
 * Reflection-probe re-rendering from the Unity implementation has no
 * direct web equivalent yet — the viewport keeps its studio-HDR IBL
 * (`EngineService.configureCampfireEnvironment`) regardless of skybox.
 */
export class SkyboxModel implements ISkyboxModel {
  readonly onSkyboxChanged = new Emitter<SkyboxRenderState>();

  private readonly m_projectModel: IProjectModel;
  private readonly m_sceneModel: ISceneModel;
  private m_state: SkyboxRenderState = DEFAULT_STATE;
  /** Guards stale async texture resolutions after rapid scene switches. */
  private m_refreshGeneration = 0;

  constructor(projectModel: IProjectModel, sceneModel: ISceneModel) {
    this.m_projectModel = projectModel;
    this.m_sceneModel = sceneModel;

    projectModel.onActiveSceneChanged.on((scene) => this.refreshSkybox(scene));
    // Mirrors Unity's `OnSceneChangesApplied`: re-apply when the active
    // scene's row mutates (covers both local writes and server events).
    projectModel.onSceneChangesApplied.on(({ sceneId }) => {
      if (projectModel.activeSceneId !== sceneId) return;
      this.refreshSkybox(projectModel.getScene(sceneId));
    });

    this.refreshSkybox(projectModel.activeScene);
  }

  get state(): SkyboxRenderState {
    return this.m_state;
  }

  get isDefaultSkybox(): boolean {
    return this.m_state.mode === SKYBOX_MODE.None;
  }

  async updateSkyboxData(skybox: SkyboxData): Promise<void> {
    const sceneId = this.m_projectModel.activeSceneId;
    const scene = sceneId ? this.m_projectModel.getScene(sceneId) : null;
    if (!sceneId || !scene) return;

    const changedMode = scene.skybox.mode !== skybox.mode;
    await this.m_projectModel.updateSkyboxDataAsync(sceneId, skybox);
    if (changedMode) {
      await this.m_sceneModel.setDefaultEnvironmentAsync();
    }
    this.refreshSkybox(this.m_projectModel.getScene(sceneId));
  }

  refreshSkybox(scene: SceneInfo | null): void {
    const generation = ++this.m_refreshGeneration;
    if (!scene) {
      this.setState(DEFAULT_STATE);
      return;
    }

    const skybox = scene.skybox;
    switch (skybox.mode) {
      case SKYBOX_MODE.Solid:
        this.setState({
          mode: SKYBOX_MODE.Solid,
          color: skybox.solidColor,
          rotation: 0,
          scale: { x: 1, y: 1 },
          intensity: skybox.intensity,
          textureUrl: null,
          textureFormat: null,
        });
        return;
      case SKYBOX_MODE.Custom: {
        const cachedUrl = this.m_projectModel.getSkyboxUrl(scene.id);
        this.setState({
          mode: SKYBOX_MODE.Custom,
          color: skyboxColorForMode(skybox),
          rotation: skybox.rotation,
          scale: skybox.scale,
          intensity: skybox.intensity,
          textureUrl: cachedUrl,
          textureFormat: skybox.format,
        });
        if (!cachedUrl) {
          // Unity shows the default skybox until the texture arrives
          // (`SetCustomSkybox`: `m_mode = texture == null ? None : Custom`);
          // the web keeps Custom mode but with a null texture, which the
          // renderer treats the same way.
          void this.m_projectModel
            .loadSkyboxUrlAsync(scene.id)
            .then((url) => {
              if (!url || generation !== this.m_refreshGeneration) return;
              this.setState({ ...this.m_state, textureUrl: url });
            })
            .catch((err: unknown) => {
              console.warn("[SkyboxModel] skybox texture load failed", err);
            });
        }
        return;
      }
      case SKYBOX_MODE.None:
      default:
        this.setState(DEFAULT_STATE);
    }
  }

  setDefaultSkybox(): void {
    this.setState(DEFAULT_STATE);
  }

  setSolidSkybox(color: RgbaColor, intensity: number): void {
    this.setState({
      mode: SKYBOX_MODE.Solid,
      color,
      rotation: 0,
      scale: { x: 1, y: 1 },
      intensity: Math.max(0, intensity),
      textureUrl: null,
      textureFormat: null,
    });
  }

  setActiveSceneCustomSkybox(
    color: RgbaColor,
    rotation: number,
    scale: { x: number; y: number },
    intensity: number,
  ): void {
    const sceneId = this.m_projectModel.activeSceneId;
    const scene = sceneId ? this.m_projectModel.getScene(sceneId) : null;
    if (!sceneId || !scene) return;
    this.setState({
      mode: SKYBOX_MODE.Custom,
      color,
      rotation,
      scale,
      intensity: Math.max(0, intensity),
      textureUrl: this.m_projectModel.getSkyboxUrl(sceneId),
      textureFormat: scene.skybox.format,
    });
  }

  private setState(state: SkyboxRenderState): void {
    this.m_state = state;
    this.onSkyboxChanged.emit(state);
  }
}

export const SKYBOX_MODEL = Symbol.for("Campfire.SkyboxModel");
